export const getProtectedMediaHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const MAX_BLOB_CACHE_ENTRIES = 160;
const blobCache = new Map(); // url -> { objectUrl, blob, timestamp }

export const getCachedObjectUrl = (url) => {
  if (!url) return null;
  const entry = blobCache.get(url);
  if (entry?.objectUrl) {
    return entry.objectUrl;
  }
  return null;
};

export const fetchProtectedBlobWithResponse = async (url, onProgress) => {
  if (!url) {
    throw new Error('URL не указан');
  }

  // 1. Проверяем кэш в памяти
  const cached = blobCache.get(url);
  if (cached?.objectUrl && cached?.blob) {
    if (typeof onProgress === 'function') {
      try { onProgress(100); } catch { /* ignore */ }
    }
    return {
      blob: cached.blob,
      objectUrl: cached.objectUrl,
      fromCache: true,
      response: null,
    };
  }

  const response = await fetch(url, {
    headers: getProtectedMediaHeaders(),
  });

  if (!response.ok) {
    let errorMessage = `HTTP ${response.status}`;
    try {
      const contentType = response.headers.get('content-type') || '';
      if (contentType.includes('application/json')) {
        const data = await response.json();
        errorMessage = data?.detail || data?.message || errorMessage;
      }
    } catch (error) {
      console.warn('Не удалось разобрать ошибку protected media:', error);
    }
    throw new Error(errorMessage);
  }

  const contentLength = Number(response.headers.get('content-length')) || 0;
  let blob;

  // Если доступен ReadableStream и известен размер файла — читаем потоком с прогрессом
  if (response.body && contentLength > 0 && typeof onProgress === 'function') {
    const reader = response.body.getReader();
    let receivedBytes = 0;
    const chunks = [];

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      receivedBytes += value.length;
      const percent = Math.min(99, Math.round((receivedBytes / contentLength) * 100));
      try { onProgress(percent); } catch { /* ignore */ }
    }

    const contentType = response.headers.get('content-type') || 'image/jpeg';
    blob = new Blob(chunks, { type: contentType });
    try { onProgress(100); } catch { /* ignore */ }
  } else {
    blob = await response.blob();
    if (typeof onProgress === 'function') {
      try { onProgress(100); } catch { /* ignore */ }
    }
  }

  const objectUrl = URL.createObjectURL(blob);

  // Добавляем в кэш с ограничением размера
  if (blobCache.size >= MAX_BLOB_CACHE_ENTRIES) {
    const oldestKey = blobCache.keys().next().value;
    const oldEntry = blobCache.get(oldestKey);
    if (oldEntry?.objectUrl) {
      try { URL.revokeObjectURL(oldEntry.objectUrl); } catch { /* ignore */ }
    }
    blobCache.delete(oldestKey);
  }
  blobCache.set(url, { objectUrl, blob, timestamp: Date.now() });

  return {
    blob,
    objectUrl,
    fromCache: false,
    response,
  };
};

export const fetchProtectedBlob = async (url, onProgress) => {
  const { blob } = await fetchProtectedBlobWithResponse(url, onProgress);
  return blob;
};

/**
 * Загрузка файла через XMLHttpRequest с отслеживанием прогресса (0-100%).
 */
export const uploadProtectedFileWithProgress = ({ url, formData, onProgress }) => {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', url, true);

    const token = localStorage.getItem('token');
    if (token) {
      xhr.setRequestHeader('Authorization', `Bearer ${token}`);
    }

    if (xhr.upload && typeof onProgress === 'function') {
      xhr.upload.onprogress = (event) => {
        if (event.lengthComputable && event.total > 0) {
          const percent = Math.min(99, Math.round((event.loaded / event.total) * 100));
          try { onProgress(percent); } catch { /* ignore */ }
        }
      };
    }

    xhr.onload = () => {
      if (typeof onProgress === 'function') {
        try { onProgress(100); } catch { /* ignore */ }
      }
      const contentType = xhr.getResponseHeader('content-type') || '';
      let data = {};
      try {
        if (contentType.includes('application/json')) {
          data = JSON.parse(xhr.responseText);
        }
      } catch {
        data = {};
      }

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(data);
      } else {
        const errorDetail = data?.detail || data?.message || `Ошибка загрузки (${xhr.status})`;
        reject(new Error(errorDetail));
      }
    };

    xhr.onerror = () => {
      reject(new Error('Сетевая ошибка при загрузке фото'));
    };

    xhr.ontimeout = () => {
      reject(new Error('Превышено время ожидания загрузки фото'));
    };

    xhr.send(formData);
  });
};
